import { type IContent } from "@kunduz/data-access-cms-api";
import {
  ColorType,
  LandingPageTable,
  SectionHeader,
  blue,
  gray,
  green,
  pink,
  red,
} from "@kunduz/ui-components";
import { Badge } from "@kunduz/ui-molecules";
import { Check, Minus, XClose } from "@untitled-ui/icons-react";
import React, { ReactElement } from "react";
import styles from "./tableWithContext.module.scss";
import { trackEventNext } from "@kunduz/utility-analytics";

export const TableWithContext = (props: Props): ReactElement => {
  const columnIdArray = props.data.columns.map((x) => x.id);

  const TableHeaderRow = ({
    data,
  }: {
    data: IContent.TableComponentData;
  }): ReactElement => {
    const _colors: Record<string, ColorType> = {
      blue,
      pink,
      red,
      green,
      gray,
      white: gray,
    };

    return (
      <LandingPageTable.Row>
        <LandingPageTable.HeaderCell />
        {data.columns.map((header, index) => (
          <LandingPageTable.HeaderCell
            style={{ width: header.width || "auto" }}
            key={`header-${index}`}
          >
            <p style={{ color: _colors[header.theme || "gray"][600] as any }}>
              {header.image ? (
                <img
                  src={header.image}
                  alt=""
                  loading="lazy"
                  className={styles.headerImage}
                />
              ) : (
                header.title
              )}
            </p>
          </LandingPageTable.HeaderCell>
        ))}
      </LandingPageTable.Row>
    );
  };

  const TableBody = ({
    data,
  }: {
    data: IContent.TableComponentData;
  }): ReactElement => {
    const ValueCell = ({
      data,
    }: {
      data?: IContent.TableComponentData["cells"][0];
    }): ReactElement => {
      return (
        <>
          {data?.type === "boolean" && (
            <>
              <Badge
                variant={"icon"}
                size="sm"
                color={
                  data.value === "true"
                    ? "green"
                    : data.value === "neutral"
                      ? "gray"
                      : "red"
                }
              >
                {data.value === "true" ? (
                  <Check />
                ) : data.value === "neutral" ? (
                  <Minus />
                ) : (
                  <XClose />
                )}
              </Badge>
              {data.description && (
                <>
                  <div style={{ height: 2 }} />
                  <LandingPageTable.TwoLevelText label2={data.description} />
                </>
              )}
            </>
          )}
          {data?.type === "text" && (
            <LandingPageTable.TwoLevelText
              label1={data.value as string}
              label2={data.description}
            />
          )}
        </>
      );
    };

    return (
      <>
        {data.rows.map((row, rowIndex) => (
          <LandingPageTable.Row key={`row_${rowIndex}`}>
            <LandingPageTable.Cell>
              <LandingPageTable.TwoLevelText
                label1={row.title}
                label2={row.description}
              />
            </LandingPageTable.Cell>
            {columnIdArray.map((columnId) => (
              <LandingPageTable.Cell key={`column_${columnId}`}>
                <ValueCell
                  data={data.cells.find(
                    (cell) =>
                      cell.row_id === row.id && cell.column_id === columnId,
                  )}
                />
              </LandingPageTable.Cell>
            ))}
          </LandingPageTable.Row>
        ))}
      </>
    );
  };

  const TableFooter = ({
    data,
  }: {
    data: IContent.TableComponentData;
  }): ReactElement => {
    const handleClick = (label: string, url: string) => {
      trackEventNext({
        event: "Table Component Clicked",
        label,
        url,
      });
    };

    return (
      <LandingPageTable.Row>
        <LandingPageTable.FooterCell />
        {data.columns.map((header, index) => (
          <LandingPageTable.FooterCell key={`footer-${index}`}>
            {header.button_label && (
              <LandingPageTable.FooterCta
                href={header.button_url}
                label={header.button_label}
                theme={header.theme}
                target={header.button_target}
                onClick={() =>
                  handleClick(header.button_label, header.button_url)
                }
              />
            )}
          </LandingPageTable.FooterCell>
        ))}
      </LandingPageTable.Row>
    );
  };

  return (
    <section
      className={styles.section}
      id={props.data.anchor_tag}
      style={props.style}
    >
      <SectionHeader
        align={props.data.alignment as any}
        badgeText={props.data.badge}
        title={props.data.title}
        desc={props.data.description}
        size={props.data.size}
      />
      <LandingPageTable.Wrapper>
        <div className={styles.tableWrapper}>
          <LandingPageTable.Body striped>
            <TableHeaderRow data={props.data} />
            <TableBody data={props.data} />
            <TableFooter data={props.data} />
          </LandingPageTable.Body>
        </div>
      </LandingPageTable.Wrapper>

      {props.data.disclaimer_text && (
        <div
          className={styles.disclaimer}
          dangerouslySetInnerHTML={{ __html: props.data.disclaimer_text }}
        ></div>
      )}
    </section>
  );
};

interface Props {
  data: IContent.TableComponentData;
  style?: any;
}
